import type { NorthStarKpi } from "@nexus/contracts"
import { CARD_DESCRIPTORS } from "@/features/dashboard/lib/metrics"
import { resolveDupBadgeCount } from "./drilldown-card-format"
import type { MyBrandCreativeCardModel } from "./drilldown-card.types"
import { formatTableValue, type TableValueFormat } from "./format-table-value"
import { formatYmd, extractNumber } from "./drilldown-row"
import type { DrilldownQuerySection } from "@/lib/queries/drilldown.queries"
import type { ParsedMyBrandDrawerData } from "./parse-my-brand-drawer-data"
import { mapMyBrandDrawerNarrative } from "./my-brand-drawer-narrative"

export interface MyBrandDrawerModel {
  id: string
  brandName: string
  logoUrl: string | null
  isVideo: boolean
  thumbnailUrl: string
  assetUrl: string
  adLibUrl: string
  launchedDate: string
  spend: string
  duplications: string
  northStarValue: string
  presenterAge: string
  targetUserPersona: string
  topics: string
  userPersona: string
}

const NORTH_STAR_DESCRIPTOR = CARD_DESCRIPTORS[1]!

function toTableFormat(format: ReturnType<typeof NORTH_STAR_DESCRIPTOR.format>): TableValueFormat {
  return format === "count" ? "number" : format
}

function northStarRawValue(rows: Array<Record<string, unknown>>, northStar: NorthStarKpi): number | null {
  const row = rows[0]
  if (!row) return null
  if (northStar === "roas") return extractNumber(row.roas)
  if (northStar === "cpa") return extractNumber(row.cpa)
  return extractNumber(row.custom_event)
}

function sumSpend(rows: Array<Record<string, unknown>>): number | null {
  if (rows.length === 0) return null
  const first = extractNumber(rows[0]?.spend)
  if (first !== null) return first
  return rows.reduce((acc, row) => acc + (extractNumber(row.spend) ?? 0), 0)
}

function drawerDuplications(
  rows: Array<Record<string, unknown>>,
  creative: MyBrandCreativeCardModel,
  emptyValue: string,
): string {
  const ads = extractNumber(rows[0]?.ads)
  if (ads !== null) {
    const dup = resolveDupBadgeCount(ads)
    return dup !== null ? String(dup) : emptyValue
  }
  return creative.dupCount !== null ? String(creative.dupCount) : emptyValue
}

export function mapMyBrandDrawerModel(
  creative: MyBrandCreativeCardModel,
  section: DrilldownQuerySection | null,
  parsed: ParsedMyBrandDrawerData | null,
  northStar: NorthStarKpi,
  emptyValue: string,
  comingSoonLabel: string,
  noneLabel: string,
  brandName: string,
  brandLogoUrl: string | null,
): MyBrandDrawerModel {
  const rows = section?.rows ?? []
  const narrative = mapMyBrandDrawerNarrative(parsed)
  const nsFormat = NORTH_STAR_DESCRIPTOR.format(northStar)
  const nsValue = northStarRawValue(rows, northStar)
  const spendValue = sumSpend(rows)

  return {
    id: creative.id,
    brandName,
    logoUrl: brandLogoUrl,
    isVideo: creative.isVideo,
    thumbnailUrl: creative.thumbnailUrl,
    assetUrl: creative.assetUrl,
    adLibUrl: creative.adLibUrl,
    launchedDate: creative.minDate ? formatYmd(creative.minDate) : emptyValue,
    spend: spendValue !== null ? formatTableValue(spendValue, "currency") : emptyValue,
    duplications: drawerDuplications(rows, creative, emptyValue),
    northStarValue: nsValue !== null ? formatTableValue(nsValue, toTableFormat(nsFormat)) : emptyValue,
    presenterAge: narrative.presenterAge ?? emptyValue,
    targetUserPersona: narrative.targetAudience ?? noneLabel,
    topics: narrative.topics ?? emptyValue,
    userPersona: comingSoonLabel,
  }
}
